/**
 * Transaction Action Modals
 * Provides modal dialogs for transaction bulk actions
 */

import { format } from 'date-fns';
import { CalendarIcon, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AddContactDialog } from '@/components/shared/add-contact-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useCompany } from '@/contexts/CompanyContext';
import { useCustomerContactsList } from '@/hooks/useCustomerContacts';
import { useCustomerDetails } from '@/hooks/useCustomerDetailData';
import { useCreateDispute, useDisputeTypeOptions } from '@/hooks/useDisputesData';
import { useAddBulkTransactionNotes } from '@/hooks/useTransactionNotes';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { useWorkflowStages, useWorkflowsList } from '@/hooks/useWorkflowsData';
import { contactDisplayName } from '@/lib/contact-display-name';
import { resolveLevelOneAssignee } from '@/lib/dispute-assignee';
import { calculateTransactionTotals } from '@/lib/export-utils';
import { cn, formatCurrency, getCurrencySymbol } from '@/lib/utils';
import type { Transaction } from '@/types/transaction';

// Types
interface TransactionActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTransactions: Transaction[];
}

interface CreateCollectionCaseModalProps extends TransactionActionModalProps {
  onSave?: (data: CollectionCaseFormData) => void;
}

interface CreateDisputeModalProps extends TransactionActionModalProps {
  onSave?: (data: DisputeFormData) => void;
}

interface AddNoteModalProps extends TransactionActionModalProps {
  onSave?: (data: NoteFormData) => void;
}

export interface CollectionCaseFormData {
  customerIds: string[];
  priority: 'High' | 'Medium' | 'Low';
  strategyId: string;
  assignedTo?: string;
  notes: string;
  nextAction: string;
  nextActionDate: Date;
  transactionIds: number[];
}

export interface DisputeFormData {
  customerId: string;
  disputeTypeId: string;
  amount: number;
  priority: string;
  description: string;
  transactionIds: number[];
}

export interface NoteFormData {
  customerIds: string[];
  content: string;
  transactionIds: number[];
}

/**
 * Create Collection Case Modal
 */
export function CreateCollectionCaseModal({
  open,
  onOpenChange,
  selectedTransactions,
  onSave,
}: CreateCollectionCaseModalProps) {
  const { selectedCompany } = useCompany();
  const { data: activeUsers = [] } = useCompanyUsersList();

  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [strategyId, setStrategyId] = useState('strategy-1');
  const [assignedTo, setAssignedTo] = useState<string>('unassigned');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('Follow-up call');
  const [nextActionDate, setNextActionDate] = useState<Date>(new Date());
  const [nextActionDatePickerOpen, setNextActionDatePickerOpen] = useState(false);

  // Safety check - moved after all hooks
  if (!selectedTransactions || selectedTransactions.length === 0) {
    return null;
  }

  // Available collection strategies
  const availableStrategies = [
    { id: 'strategy-1', name: 'Standard Collection', type: 'Standard' },
    { id: 'strategy-2', name: 'Intensive Follow-up', type: 'Intensive' },
    { id: 'strategy-3', name: 'VIP Treatment', type: 'VIP' },
    { id: 'strategy-4', name: 'High Risk Protocol', type: 'High Risk' },
  ];

  // Get unique customers from selected transactions
  const uniqueCustomers = Array.from(
    new Map(
      selectedTransactions.map((t) => [t.customerId, { id: t.customerId, name: t.customerName || 'Unknown Customer' }]),
    ).values(),
  );

  // Calculate total amount
  const totalAmount = selectedTransactions.reduce((sum, t) => sum + (t.outstandingAmount || t.amount || 0), 0);
  const currency = selectedTransactions[0]?.currency || selectedCompany?.currency || 'GBP';

  const handleSave = () => {
    // Validation
    if (!strategyId) {
      toast.error('Please select a collection strategy');
      return;
    }
    if (!nextAction.trim()) {
      toast.error('Please enter a next action');
      return;
    }

    const formData: CollectionCaseFormData = {
      customerIds: uniqueCustomers.map((c) => c.id.toString()),
      priority,
      strategyId,
      assignedTo: assignedTo === 'unassigned' ? undefined : assignedTo,
      notes,
      nextAction,
      nextActionDate,
      transactionIds: selectedTransactions.map((t) => t.id),
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success(`Collection case${uniqueCustomers.length > 1 ? 's' : ''} created successfully`);
    }

    // Reset form
    setPriority('Medium');
    setStrategyId('strategy-1');
    setAssignedTo('unassigned');
    setNotes('');
    setNextAction('Follow-up call');
    setNextActionDate(new Date());
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[600px] bg-white dark:bg-gray-950 overflow-hidden">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle>Create Collection Case</SheetTitle>
          <SheetDescription>
            Create collection case{uniqueCustomers.length > 1 ? 's' : ''} for {selectedTransactions.length} selected
            transaction{selectedTransactions.length > 1 ? 's' : ''}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4">
          <div className="grid gap-6 py-6">
            {/* Summary */}
            <div className="space-y-2">
              <Label>Summary</Label>
              <div className="rounded-lg border p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Transactions:</span>
                  <span className="font-medium">{selectedTransactions.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Customers:</span>
                  <span className="font-medium">{uniqueCustomers.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Amount:</span>
                  <span className="font-semibold">{formatCurrency(totalAmount, currency)}</span>
                </div>
              </div>
            </div>

            {/* Customer List */}
            {uniqueCustomers.length > 1 && (
              <div className="space-y-2">
                <Label>Customers ({uniqueCustomers.length})</Label>
                <div className="rounded-lg border p-3 max-h-32 overflow-y-auto">
                  <div className="space-y-1">
                    {uniqueCustomers.map((customer) => (
                      <div key={customer.id} className="text-sm">
                        {customer.name}
                      </div>
                    ))}
                  </div>
                </div>
                <Alert>
                  <AlertDescription className="text-xs">
                    A separate collection case will be created for each customer
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {uniqueCustomers.length === 1 && (
              <div className="space-y-2">
                <Label>Customer</Label>
                <Input value={uniqueCustomers[0].name} disabled />
              </div>
            )}

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="priority">Priority *</Label>
              <Select value={priority} onValueChange={(value) => setPriority(value as 'High' | 'Medium' | 'Low')}>
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Collection Strategy */}
            <div className="space-y-2">
              <Label htmlFor="strategy">Collection Strategy *</Label>
              <Select value={strategyId} onValueChange={setStrategyId}>
                <SelectTrigger id="strategy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableStrategies.map((strategy) => (
                    <SelectItem key={strategy.id} value={strategy.id}>
                      {strategy.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assigned To */}
            <div className="space-y-2">
              <Label htmlFor="assigned-to">Assign To</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger id="assigned-to">
                  <SelectValue placeholder="Select user (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {activeUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.firstName} {user.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Next Action */}
            <div className="space-y-2">
              <Label htmlFor="next-action">Next Action *</Label>
              <Input
                id="next-action"
                placeholder="e.g., Follow-up call, Send reminder email"
                value={nextAction}
                onChange={(e) => setNextAction(e.target.value)}
              />
            </div>

            {/* Next Action Date */}
            <div className="space-y-2">
              <Label htmlFor="next-action-date">Next Action Date *</Label>
              <Popover open={nextActionDatePickerOpen} onOpenChange={setNextActionDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="next-action-date"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !nextActionDate && 'text-muted-foreground',
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {nextActionDate ? format(nextActionDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={nextActionDate}
                    onSelect={(date) => {
                      if (date) setNextActionDate(date);
                      setNextActionDatePickerOpen(false);
                    }}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add any additional notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </div>

        <SheetFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Create Case{uniqueCustomers.length > 1 ? 's' : ''}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Create Dispute Modal
 */
export function CreateDisputeModal({ open, onOpenChange, selectedTransactions, onSave }: CreateDisputeModalProps) {
  const [disputeTypeId, setDisputeTypeId] = useState('');
  const [amount, setAmount] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [contactId, setContactId] = useState('');
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const pendingNewContactId = useRef<string | null>(null);
  const defaultAssigneeAppliedFor = useRef<string | null>(null);

  const createDisputeMutation = useCreateDispute();

  // Active users of the current company, offered as assignees
  const { data: users = [] } = useCompanyUsersList();

  const { data: workflows = [] } = useWorkflowsList();
  const workflowIdNum = useMemo(() => {
    if (!disputeTypeId) return null;
    const matchingWorkflow = workflows.find(
      (w) => w.disputeTypeId === parseInt(disputeTypeId, 10) && w.status.toLowerCase() === 'active',
    );
    return matchingWorkflow ? matchingWorkflow.id : null;
  }, [disputeTypeId, workflows]);
  const { data: workflowStages = [] } = useWorkflowStages(workflowIdNum);

  // Get the customer for the selected transactions
  const uniqueCustomers = Array.from(new Set(selectedTransactions.map((t) => t.customerId)));
  const customerName = selectedTransactions[0]?.customerName ?? '';

  // Contacts for the customer
  const customerId = uniqueCustomers.length === 1 ? uniqueCustomers[0].toString() : '0';
  const { data: contactsData = [] } = useCustomerContactsList(customerId);

  // Customer details supply the credit controller a workflow level can delegate the assignment to
  const { data: customer, isLoading: isCustomerLoading } = useCustomerDetails(
    uniqueCustomers.length === 1 ? customerId : undefined,
  );

  const assigneeOptions = useMemo(
    () =>
      users
        .map((user) => ({ id: user.id, displayName: `${user.firstName} ${user.lastName}` }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [users],
  );

  const sortedStages = useMemo(() => [...workflowStages].sort((a, b) => a.sortOrder - b.sortOrder), [workflowStages]);
  const levelOneProperties = sortedStages[0]?.properties;

  // Auto-select pending new contact after refetch
  useEffect(() => {
    if (pendingNewContactId.current && contactsData.length > 0) {
      const newContact = contactsData.find((c) => c.id === pendingNewContactId.current);
      if (newContact) {
        setContactId(newContact.id);
        pendingNewContactId.current = null;
      }
    }
  }, [contactsData]);

  // Calculate total amount using the same logic as the selection toolbar
  const totals = calculateTransactionTotals(selectedTransactions);
  const totalAmount = totals.totalOutstanding;
  const currency = totals.currency;
  const transactionCurrencySymbol = getCurrencySymbol(currency);

  // Dispute types - fetch from API (includes both system and manual types)
  const { data: disputeTypes = [] } = useDisputeTypeOptions();

  // Pre-fill amount with total when modal opens, discard everything entered once it closes
  useEffect(() => {
    if (open) {
      setAmount(totalAmount.toFixed(2));
      return;
    }

    setDisputeTypeId('');
    setAmount('');
    setPriority('medium');
    setDescription('');
    setAssignedUserId('');
    setContactId('');
    pendingNewContactId.current = null;
    defaultAssigneeAppliedFor.current = null;
  }, [open, totalAmount]);

  // Clear the assignee whenever the dispute type changes so the new type's default can be applied
  useEffect(() => {
    defaultAssigneeAppliedFor.current = null;
    setAssignedUserId('');
  }, [disputeTypeId]);

  // Default the assignee to the chosen dispute type's level 1 assigned user
  useEffect(() => {
    if (!disputeTypeId || defaultAssigneeAppliedFor.current === disputeTypeId) return;
    if (!levelOneProperties || users.length === 0 || isCustomerLoading) return;

    const levelOneAssignee = resolveLevelOneAssignee(levelOneProperties, users, customer?.creditControllerId);
    defaultAssigneeAppliedFor.current = disputeTypeId;
    setAssignedUserId(levelOneAssignee ? String(levelOneAssignee) : '');
  }, [disputeTypeId, levelOneProperties, users, customer?.creditControllerId, isCustomerLoading]);

  const priorityToId: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4 };

  const handleSave = async () => {
    // Validation
    if (uniqueCustomers.length !== 1) {
      toast.error('Disputes can only be created for transactions from a single customer');
      return;
    }
    if (!disputeTypeId) {
      toast.error('Please select a dispute type');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid dispute amount');
      return;
    }
    if (parseFloat(amount) > totalAmount) {
      toast.error('Dispute amount cannot exceed total transaction amount');
      return;
    }
    if (!assignedUserId) {
      toast.error('Please select an assigned user');
      return;
    }

    setIsSaving(true);
    try {
      // Get due date from stage 1's followUpDays
      let dueDate: string | undefined;
      if (sortedStages[0]?.properties) {
        try {
          const props = JSON.parse(sortedStages[0].properties);
          const followUpDays =
            props.followUpDays ?? (props.slaWarningHours ? Math.ceil(props.slaWarningHours / 24) : undefined);
          if (followUpDays && typeof followUpDays === 'number') {
            const due = new Date();
            due.setDate(due.getDate() + followUpDays);
            dueDate = due.toISOString().split('T')[0];
          }
        } catch {
          /* ignore parse errors */
        }
      }

      const response = await createDisputeMutation.mutateAsync({
        customerId: uniqueCustomers[0],
        disputeTypeId: parseInt(disputeTypeId, 10),
        amount: parseFloat(amount),
        statusId: 1,
        priorityId: priorityToId[priority] ?? 3,
        workflowId: workflowIdNum ?? undefined,
        assignedToUserId: parseInt(assignedUserId, 10),
        dueDate,
        totalLevels: workflowStages.length > 0 ? workflowStages.length : undefined,
        description: description || undefined,
        transactionIds: selectedTransactions.map((t) => t.id),
        currencyCode: currency,
        contactId: contactId ? parseInt(contactId, 10) : undefined,
      });

      toast.success(`Dispute ${response.disputeNumber} created successfully`, {
        duration: 5000,
      });

      if (onSave) {
        onSave({
          customerId: uniqueCustomers[0].toString(),
          disputeTypeId,
          amount: parseFloat(amount),
          priority,
          description,
          transactionIds: selectedTransactions.map((t) => t.id),
        });
      }

      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to create dispute: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Dispute</DialogTitle>
          <DialogDescription>
            Flag {selectedTransactions.length} transaction{selectedTransactions.length > 1 ? 's' : ''} as disputed
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input id="customer" value={customerName} disabled />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="contact" className="flex items-center gap-1">
              Contact
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 ml-1 p-0"
                onClick={() => setAddContactDialogOpen(true)}
                title="Add new contact"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </Label>
            <Select value={contactId} onValueChange={setContactId}>
              <SelectTrigger id="contact">
                <SelectValue placeholder="Select contact (optional)" />
              </SelectTrigger>
              <SelectContent>
                {contactsData.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {contactDisplayName(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="total-amount">Total Amount</Label>
            <Input id="total-amount" value={formatCurrency(totalAmount, currency)} disabled />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="dispute-type">
              Dispute Type <span className="text-red-500">*</span>
            </Label>
            <Select value={disputeTypeId} onValueChange={setDisputeTypeId}>
              <SelectTrigger id="dispute-type">
                <SelectValue placeholder="Select dispute type" />
              </SelectTrigger>
              <SelectContent>
                {disputeTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="assigned-user">
              Assigned User <span className="text-red-500">*</span>
            </Label>
            <Select value={assignedUserId} onValueChange={setAssignedUserId} disabled={!disputeTypeId}>
              <SelectTrigger id="assigned-user">
                <SelectValue placeholder="Select assigned user" />
              </SelectTrigger>
              <SelectContent>
                {assigneeOptions.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="dispute-amount">
              Dispute Amount ({transactionCurrencySymbol}) <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                {transactionCurrencySymbol}
              </span>
              <Input
                id="dispute-amount"
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="priority">
              Priority <span className="text-red-500">*</span>
            </Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe the dispute..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!disputeTypeId || !assignedUserId || !amount || parseFloat(amount) <= 0 || !priority || isSaving}
          >
            {isSaving ? 'Creating...' : 'Create Dispute'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AddContactDialog
        customerId={customerId}
        defaultCompany={customerName}
        open={addContactDialogOpen}
        onOpenChange={setAddContactDialogOpen}
        onContactCreated={(newId) => {
          pendingNewContactId.current = newId;
          setContactId(newId);
        }}
      />
    </Dialog>
  );
}

/**
 * Add Note Modal
 */
export function AddNoteModal({ open, onOpenChange, selectedTransactions, onSave }: AddNoteModalProps) {
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const addBulkNotesMutation = useAddBulkTransactionNotes();

  useEffect(() => {
    if (!open) {
      setContent('');
    }
  }, [open]);

  // Get unique customers
  const uniqueCustomers = Array.from(
    new Map(selectedTransactions.map((t) => [t.customerId, { id: t.customerId, name: t.customerName }])).values(),
  );

  const transactionCount = selectedTransactions.length;

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error('Please enter note content');
      return;
    }

    setIsSaving(true);
    try {
      await addBulkNotesMutation.mutateAsync({
        transactionIds: selectedTransactions.map((t) => t.id),
        content,
        userId: '0',
        userName: 'Current User',
      });

      toast.success(
        transactionCount === 1 ? 'Note added successfully' : `Note added to ${transactionCount} transactions`,
      );

      if (onSave) {
        onSave({
          customerIds: uniqueCustomers.map((c) => c.id.toString()),
          content,
          transactionIds: selectedTransactions.map((t) => t.id),
        });
      }

      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to add note: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>
            Add a note for {transactionCount} selected transaction{transactionCount > 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Summary */}
          <div className="space-y-2">
            <Label>Summary</Label>
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Transactions:</span>
                <span className="font-medium">
                  {transactionCount <= 5
                    ? selectedTransactions.map((t) => t.referenceNumber).join(', ')
                    : `${transactionCount} transactions selected`}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Customer:</span>
                <span className="font-medium">{uniqueCustomers[0]?.name || 'N/A'}</span>
              </div>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="content">Note Content *</Label>
            <Textarea
              id="content"
              placeholder="Enter note content..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
